/**
 * The paged list, written once. R-272.
 *
 * Six screens had their own copy of the same twelve lines: a page number, a
 * search box debounced at 250ms, `keepPreviousData` so turning a page does not
 * blank the table, and a reset to page one whenever a filter changes. The
 * debounce block was identical to the character in five of them.
 *
 * That is not a style complaint. Each copy is a place the next bug in this
 * pattern has to be fixed again, and the reset-to-page-one is exactly the kind
 * of rule that gets forgotten in the sixth copy: filter to "Overdue" while on
 * page 8 of 12, and the screen asks for page 8 of a list that has two, then
 * shows nothing and looks broken.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ──────────────────────────────────────
 *
 * It does not render anything. The six tables are genuinely different: one is
 * virtualised, one carries inline editors, one expands a panel underneath the
 * row. A component that absorbed all three would grow a prop for every
 * difference and be harder to read than the duplication it replaced.
 *
 * So this owns the STATE, which is identical everywhere, and leaves the markup
 * alone, which is not.
 */
import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/react-query';

/** How long to wait after the last keystroke. */
export const SEARCH_DEBOUNCE_MS = 250;

export interface PagedListState<T> {
  /** The page being shown, one based. */
  page: number;
  setPage: (page: number) => void;
  /** What is in the search box, updated on every keystroke. */
  query: string;
  setQuery: (query: string) => void;
  /** What has been sent to the server, updated after the debounce. */
  needle: string;
  rows: T[];
  /** Everything the pager needs, with the shapes it expects when there is no data yet. */
  pager: { page: number; pages: number; total: number; limit: number; busy: boolean };
  data: unknown;
  isFetching: boolean;
  /**
   * `Error | null`, not `unknown`.
   *
   * `unknown` makes `{error && <Callout/>}` evaluate to `unknown`, which is not
   * a ReactNode, so every caller has to cast before it can render the thing.
   * A hook that forces a cast at each of six call sites has moved the work
   * rather than removed it.
   */
  error: Error | null;
}

/**
 * @param makeQuery Builds the query options from the page and the search term.
 * @param resetOn   Values that send the reader back to page one when they change.
 *                  A filter belongs here. Forgetting it is the page 8 of 2 bug.
 * @param initial   Where to start, for a screen that keeps its place in the
 *                  URL. Opening a record unmounts the list, so a page number
 *                  held only here is gone when the reader comes back. The
 *                  orders register passes what the query string says.
 */
export function usePagedList<T>(
  makeQuery: (params: { page: number; q: string }) => UseQueryOptions<any, any, any, any>,
  resetOn: unknown[] = [],
  initial: { page?: number; query?: string } = {},
): PagedListState<T> {
  const [page, setPage] = useState(initial.page ?? 1);
  const [query, setQuery] = useState(initial.query ?? '');
  // Seeded, or the first render asks for page 3 of an unfiltered list while
  // the debounce catches up, which is a wasted request and a visible flicker.
  const [needle, setNeedle] = useState(initial.query ?? '');

  // Debounced, so a six letter search is one request rather than six.
  useEffect(() => {
    const timer = setTimeout(() => setNeedle(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * Back to page one whenever the list underneath changes shape.
   *
   * `needle` is always here. The caller adds its filters. Without it somebody
   * on page 8 who narrows the list is asking for page 8 of a list with two, and
   * the screen answers with nothing.
   *
   * NOT ON THE FIRST RUN. An effect fires on mount, and on mount nothing has
   * changed shape: the filter and the search are whatever they started as. It
   * was harmless while every list started on page 1, and it stopped being
   * harmless the moment one could start on page 3 from its URL, because the
   * reset ran immediately and put the reader back on page 1.
   */
  const settled = useRef(false);
  useEffect(() => {
    if (!settled.current) { settled.current = true; return; }
    setPage(1);
  }, [needle, ...resetOn]);

  const { data, isFetching, error } = useQuery({
    ...makeQuery({ page, q: needle }),
    // The old page stays on screen while the next loads, so turning a page does
    // not blank the table and jump the scroll position.
    placeholderData: keepPreviousData,
  });

  const d = data as { rows?: T[]; page?: number; pages?: number; total?: number; limit?: number };

  return {
    page, setPage, query, setQuery, needle,
    rows: d?.rows ?? [],
    pager: {
      page: d?.page ?? 1,
      pages: d?.pages ?? 1,
      total: d?.total ?? 0,
      limit: d?.limit ?? 50,
      busy: isFetching,
    },
    data, isFetching, error: (error as Error | null) ?? null,
  };
}
